
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from "sonner";
import analytics from '@/utils/analytics';
import { InviteStatus } from '@/types/user';
import { useAuth } from './AuthContext';

interface InviteContextType {
  invites: InviteStatus[];
  createInvite: (email: string) => Promise<InviteStatus>;
  revokeInvite: (code: string) => Promise<boolean>;
  validateInvite: (code: string) => Promise<boolean>;
}

const InviteContext = createContext<InviteContextType | undefined>(undefined);

export const InviteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [invites, setInvites] = useState<InviteStatus[]>([]);

  // Load invites from localStorage
  useEffect(() => {
    if (user?.isAdmin) {
      loadInvites();
    }
  }, [user]);

  // Load invites from localStorage (in real app, this would be from API)
  const loadInvites = () => {
    try {
      const savedInvites = localStorage.getItem('invites');
      if (savedInvites) {
        const parsedInvites = JSON.parse(savedInvites);
        
        // Convert date strings back to Date objects and ensure proper status type
        const processedInvites = parsedInvites.map((invite: any) => ({
          ...invite,
          createdAt: new Date(invite.createdAt),
          expiresAt: new Date(invite.expiresAt),
          acceptedAt: invite.acceptedAt ? new Date(invite.acceptedAt) : undefined,
          status: invite.status as 'pending' | 'accepted' | 'expired' | 'revoked'
        }));
        
        setInvites(processedInvites);
      }
    } catch (error) {
      console.error("Error loading invites:", error);
    }
  };

  // Save invites to localStorage
  const saveInvites = (updatedInvites: InviteStatus[]) => {
    try {
      localStorage.setItem('invites', JSON.stringify(updatedInvites));
      setInvites(updatedInvites);
    } catch (error) {
      console.error("Error saving invites:", error);
      toast.error("Failed to save invites.");
    }
  };

  // Create a new invite
  const createInvite = async (email: string): Promise<InviteStatus> => {
    try {
      if (!user?.isAdmin) {
        throw new Error("Only administrators can create invites");
      }
      
      // Generate a unique invite code
      const code = 'INV_' + Math.random().toString(36).substring(2, 10).toUpperCase();
      
      // Set expiration date to 7 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      const newInvite: InviteStatus = {
        code,
        email,
        status: 'pending',
        createdAt: new Date(),
        expiresAt,
      };
      
      // In a real app, this would send an email with the invite link
      console.log(`Invite created for ${email} with code ${code}`);
      
      // Save the invite
      const updatedInvites = [...invites, newInvite];
      saveInvites(updatedInvites);
      
      // Track this action
      analytics.trackAdminAction('create_invite', { email });
      
      toast.success(`Invite sent to ${email}`);
      return newInvite;
    } catch (error) {
      console.error("Error creating invite:", error);
      toast.error("Failed to create invite.");
      throw error;
    }
  };

  // Revoke an invite
  const revokeInvite = async (code: string): Promise<boolean> => {
    try {
      if (!user?.isAdmin) {
        throw new Error("Only administrators can revoke invites");
      }
      
      // Find the invite
      const inviteIndex = invites.findIndex(invite => invite.code === code);
      
      if (inviteIndex === -1) {
        throw new Error("Invite not found");
      }
      
      // Check if the invite is already accepted
      if (invites[inviteIndex].status === 'accepted') {
        throw new Error("Cannot revoke an accepted invite");
      }
      
      // Update the invite status
      const updatedInvites = [...invites];
      updatedInvites[inviteIndex] = {
        ...updatedInvites[inviteIndex],
        status: 'revoked'
      };
      
      // Save the updated invites
      saveInvites(updatedInvites);
      
      // Track this action
      analytics.trackAdminAction('revoke_invite', { code });
      
      toast.success("Invite revoked successfully");
      return true;
    } catch (error) {
      console.error("Error revoking invite:", error);
      toast.error(error instanceof Error ? error.message : "Failed to revoke invite.");
      return false;
    }
  };

  // Validate an invite
  const validateInvite = async (code: string): Promise<boolean> => {
    try {
      // Find the invite
      const invite = invites.find(inv => inv.code === code);
      
      if (!invite) {
        throw new Error("Invalid invite code");
      }
      
      // Check if the invite is already used or revoked
      if (invite.status === 'accepted' || invite.status === 'revoked') {
        throw new Error("This invite has already been used or revoked");
      }
      
      // Check if the invite is expired
      if (invite.expiresAt < new Date()) {
        // Update invite status to expired
        const updatedInvites = invites.map(inv => 
          inv.code === code ? { ...inv, status: 'expired' as const } : inv
        );
        saveInvites(updatedInvites);
        
        throw new Error("This invite has expired");
      }
      
      return true;
    } catch (error) {
      console.error("Error validating invite:", error);
      toast.error(error instanceof Error ? error.message : "Invalid invite code");
      return false;
    }
  };

  return (
    <InviteContext.Provider
      value={{
        invites,
        createInvite,
        revokeInvite,
        validateInvite
      }}
    >
      {children}
    </InviteContext.Provider>
  );
};

export const useInvites = () => {
  const context = useContext(InviteContext);
  if (context === undefined) {
    throw new Error('useInvites must be used within an InviteProvider');
  }
  return context;
};
